import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, ImageIcon, CheckCircle2 } from "lucide-react";
import type { LeadWithGenerations } from "@/types/firestore";

interface StatsBarProps {
  leads: LeadWithGenerations[];
}

export function StatsBar({ leads }: StatsBarProps) {
  const totalLeads = leads.length;
  const verifiedLeads = leads.filter((l) => l.verification_status === "verified").length;
  const totalGenerations = leads.reduce((acc, l) => acc + l.generations.length, 0);

  // Calculate verification rate as the "trend" for the verified stat
  const verificationRate = totalLeads > 0 ? Math.round((verifiedLeads / totalLeads) * 100) : 0;

  const stats = [
    {
      label: "Total Leads",
      value: totalLeads.toString(),
      icon: Users,
      subtitle: "All time",
      accentClass: "bg-primary/10 text-primary",
      gradientClass: "from-primary/5",
    },
    {
      label: "Verified",
      value: verifiedLeads.toString(),
      icon: CheckCircle2,
      subtitle: `${verificationRate}% rate`,
      accentClass: "bg-success/10 text-success",
      gradientClass: "from-success/5",
    },
    {
      label: "Generations",
      value: totalGenerations.toString(),
      icon: ImageIcon,
      subtitle: "AI designs",
      accentClass: "bg-accent/10 text-accent",
      gradientClass: "from-accent/5",
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-3 sm:gap-4">
      {stats.map((stat, index) => (
        <Card
          key={stat.label}
          className="relative overflow-hidden border-border/40 bg-card shadow-sm hover:shadow-md transition-shadow"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          {/* Subtle gradient accent */}
          <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradientClass} to-transparent opacity-60`} />

          <div className="relative p-4 sm:p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground truncate">
                  {stat.label}
                </p>
                <p className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-bold font-heading text-foreground tabular-nums">
                  {stat.value}
                </p>
              </div>

              {/* Icon badge */}
              <div className={`flex h-10 w-10 sm:h-11 sm:w-11 flex-shrink-0 items-center justify-center rounded-xl ${stat.accentClass}`}>
                <stat.icon className="h-5 w-5 sm:h-5 sm:w-5" />
              </div>
            </div>

            {/* Subtitle */}
            <div className="mt-3 sm:mt-4">
              <span className="text-xs text-muted-foreground">
                {stat.subtitle}
              </span>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export function StatsBarSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-3 sm:gap-4">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="overflow-hidden border-border/40 bg-card">
          <div className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-16" />
              </div>
              <Skeleton className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl" />
            </div>
            <div className="mt-3 sm:mt-4 flex items-center gap-1.5">
              <Skeleton className="h-3.5 w-3.5" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
